import {
  CostBearerAnswer as CostBearerAnswerType,
  PracticeTherapyDepartment,
  PracticeTreatmentContractType,
} from "@physiofit-digital/physiofit_types";
import { useEffect } from "react";
import { styled } from "styled-components";
import { CustomButton } from "../../../../core/components/buttons/CustomButton";
import { IconButton } from "../../../../core/components/buttons/IconButton";
import { RadioButtonWithLabel } from "../../../../core/components/buttons/RadioButtonWithLabel";
import {
  getCurrentCostBearerScreenStep,
  getCurrentPracticePatientCostBearerDetails,
  getCurrentPracticeTreatmentContracts,
} from "../../../../core/store/currentFormSlice";
import { useMainStore } from "../../../../core/store/mainStore";
import { H4, Small } from "../../../../core/styles/stringStyles";
import { useStyles } from "../../../../core/styles/theme";
import { hexWithOpacity } from "../../../../core/utils/helpers";
import {
  getDepartmentsWithVisibleCostBearers,
  getVisibleCostBearers,
  hasTreatmentContractDocument,
} from "../../utils/costBearerContractUtils";

const contractLabels: { key: PracticeTreatmentContractType; label: string }[] =
  [
    {
      key: "statutoryHealthInsurance",
      label: "Gesetzliche Krankenversicherung (GKV)",
    },
    {
      key: "privateHealthInsurance",
      label: "Private Krankenversicherung (PKV)",
    },
    { key: "professionalAssociation", label: "Berufsgenossenschaft (BG)" },
    { key: "army", label: "Bundeswehr" },
    { key: "selfPayer", label: "Selbstzahler" },
    { key: "federalAid", label: "Bundesbeihilfe (privat versichert)" },
    { key: "stateAid", label: "Landesbeihilfe (privat versichert)" },
    { key: "pensionInsurance", label: "Rentenversicherung" },
  ];

const aidCostBearerKeys: PracticeTreatmentContractType[] = [
  "federalAid",
  "stateAid",
];

// If only one of the two Beihilfe types (Bundes-/Landesbeihilfe) is uploaded,
// show the generic Beihilfe label instead of the specific federal/state one.
const getCostBearerLabel = (
  costBearer: PracticeTreatmentContractType,
  visibleCostBearers: PracticeTreatmentContractType[],
): string => {
  const onlyOneAidUploaded =
    visibleCostBearers.filter((key) => aidCostBearerKeys.includes(key))
      .length === 1;
  if (aidCostBearerKeys.includes(costBearer) && onlyOneAidUploaded) {
    return "Beihilfe (privat versichert)";
  }
  return contractLabels.find((c) => c.key === costBearer)?.label || costBearer;
};

const treatmentDepartmentLabels: {
  [key in PracticeTherapyDepartment]: string;
} = {
  physiotherapy: "Physiotherapie",
  occupationalTherapy: "Ergotherapie",
  osteopathy: "Osteopathie",
  speechTherapy: "Logopädie",
  podology: "Podologie",
  nutritionTherapy: "Ernährungstherapie",
  trainingTherapy: "Trainingstherapie",
};

export const CostBearerAnswer = () => {
  const theme = useStyles();

  const currentPatientCostBearerDetails = useMainStore(
    getCurrentPracticePatientCostBearerDetails,
  );
  const setCurrentPatientCostBearerDetails = useMainStore(
    (state) => state.setCurrentPracticePatientCostBearerDetails,
  );
  const currentCostBearerScreenStep =
    useMainStore(getCurrentCostBearerScreenStep) ?? 0;
  const setCurrentCostBearerScreenStep = useMainStore(
    (state) => state.setCurrentCostBearerScreenStep,
  );
  const currentPracticeTreatmentContracts = useMainStore(
    getCurrentPracticeTreatmentContracts,
  );

  const departments = getDepartmentsWithVisibleCostBearers(
    currentPracticeTreatmentContracts?.practiceTreatmentContracts,
  );
  const selectedDepartmentContracts =
    currentPracticeTreatmentContracts?.practiceTreatmentContracts?.[
      currentPatientCostBearerDetails?.departmentId as PracticeTherapyDepartment
    ]?.contracts;
  const costBearers = getVisibleCostBearers(selectedDepartmentContracts);
  const contractFileDetails =
    selectedDepartmentContracts?.[
      currentPatientCostBearerDetails?.costBearerId as PracticeTreatmentContractType
    ];
  const selectedCostBearerHasDocument =
    hasTreatmentContractDocument(contractFileDetails);

  useEffect(() => {
    if (
      departments.length === 1 &&
      !currentPatientCostBearerDetails?.departmentId
    ) {
      setCurrentPatientCostBearerDetails({
        departmentId: departments[0],
        termsAccepted: false,
      });
      setCurrentCostBearerScreenStep(1);
    }
  }, [
    currentPatientCostBearerDetails?.departmentId,
    departments,
    setCurrentCostBearerScreenStep,
    setCurrentPatientCostBearerDetails,
  ]);

  const handleSelectDepartment = (department: PracticeTherapyDepartment) => {
    if (department === currentPatientCostBearerDetails?.departmentId) {
      setCurrentPatientCostBearerDetails({
        termsAccepted: false,
      });
      return;
    }
    setCurrentPatientCostBearerDetails({
      departmentId: department,
      termsAccepted: false,
    });
  };
  const handleSelectCostBearer = (costBearer: string) => {
    if (costBearer === currentPatientCostBearerDetails?.costBearerId) {
      setCurrentPatientCostBearerDetails({
        departmentId: currentPatientCostBearerDetails?.departmentId,
        termsAccepted: false,
      });
      return;
    }
    const contract =
      currentPracticeTreatmentContracts?.practiceTreatmentContracts?.[
        currentPatientCostBearerDetails?.departmentId as PracticeTherapyDepartment
      ]?.contracts?.[costBearer as PracticeTreatmentContractType];
    const contractHasDocument = hasTreatmentContractDocument(contract);

    // "contract" must be omitted entirely (not set to undefined) when the cost
    // bearer has no document — Firestore rejects undefined field values (PF-5333)
    const contractSummary: CostBearerAnswerType = {
      departmentId: currentPatientCostBearerDetails?.departmentId,
      costBearerId: costBearer,
      termsAccepted: false,
      ...(contractHasDocument && contract
        ? {
            contract: {
              documentId: `${currentPatientCostBearerDetails?.departmentId}_${costBearer}`,
              documentUrl: contract.mergedFileUrl,
              displayName: contract.contractName,
            },
          }
        : {}),
    };

    setCurrentPatientCostBearerDetails(contractSummary);
  };
  const handleAcceptTerms = () => {
    setCurrentPatientCostBearerDetails({
      ...currentPatientCostBearerDetails,
      termsAccepted: !currentPatientCostBearerDetails?.termsAccepted,
    });
  };

  const renderDepartmentSelection = () => {
    return (
      <FieldsContainer>
        {departments?.map((department) => (
          <RadioButtonWithLabel
            key={department}
            isChecked={
              currentPatientCostBearerDetails?.departmentId === department
            }
            onPress={() => handleSelectDepartment(department)}
            text={treatmentDepartmentLabels[department]}
          />
        ))}
      </FieldsContainer>
    );
  };

  const renderCostBearerSelection = () => {
    return (
      <FieldsContainer>
        {costBearers?.map((costBearer) => (
          <RadioButtonWithLabel
            key={costBearer}
            isChecked={
              currentPatientCostBearerDetails?.costBearerId === costBearer
            }
            onPress={() => handleSelectCostBearer(costBearer)}
            text={getCostBearerLabel(costBearer, costBearers ?? [])}
          />
        ))}
      </FieldsContainer>
    );
  };

  const handleDownloadContract = () => {
    if (!contractFileDetails?.mergedFileUrl) {
      console.error("Contract details are missing");
      return;
    }
    window.open(contractFileDetails.mergedFileUrl, "_blank");
  };

  const renderContractSigning = () => {
    return (
      <FieldsContainer>
        <LabeledIconButton onClick={handleDownloadContract}>
          <Label>Dokument ansehen</Label>
          <IconButton iconName="Document" primaryColor={theme.colors.medium} />
        </LabeledIconButton>
        <LawfulValidityBox>
          <IconButtonContainer>
            <IconButton
              iconName="Info"
              primaryColor={hexWithOpacity(theme.colors.medium, 0.5)}
            />
            <LawfulValidityTextContainer>
              <Label>Rechtlich gültige Unterschrift</Label>
              <StyledSmall>
                Indem Sie auf “Zustimmen” klicken, erklären Sie sich damit
                einverstanden, dass Ihr Name als elektronische Unterschrift in
                das Dokument eingefügt wird.
              </StyledSmall>
            </LawfulValidityTextContainer>
          </IconButtonContainer>
        </LawfulValidityBox>
        <RadioButtonWithLabel
          isChecked={currentPatientCostBearerDetails?.termsAccepted || false}
          onPress={handleAcceptTerms}
          text="Ich habe das Dokument gelesen und stimme zu."
          checkBoxIconName="CheckBoxSquareInactive"
        />
      </FieldsContainer>
    );
  };

  return (
    <>
      {currentCostBearerScreenStep === 0 && renderDepartmentSelection()}
      {currentCostBearerScreenStep === 1 && renderCostBearerSelection()}
      {currentCostBearerScreenStep === 2 &&
        selectedCostBearerHasDocument &&
        renderContractSigning()}
    </>
  );
};

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const IconButtonContainer = styled(CustomButton)`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 0px;
`;

const Label = styled(H4)`
  color: ${(props) => props.theme.colors.dark};
  text-align: left;
`;

const LawfulValidityBox = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 15px 20px 15px;
  border-radius: 15px;
  background-color: ${(props) =>
    hexWithOpacity(props.theme.colors.medium, 0.1)};
  ${IconButtonContainer} {
    align-items: flex-start;
  }
`;

const StyledSmall = styled(Small)`
  color: ${(props) => props.theme.colors.medium};
  text-align: left;
`;

const LawfulValidityTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const LabeledIconButton = styled(CustomButton)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;
